import React, { useLayoutEffect, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { ReadingProgress } from '../components/ReadingProgress';
import { MobileBottomCTA } from '../components/MobileBottomCTA';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { useScrollAnimation } from '../utils/AnimationObserver';
import { ShieldCheck, ArrowLeft } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
const fadeUpVariant = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
function ProofImage({
  src,
  alt,
  caption
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return <div className="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200" data-id="element-838" data-mp="2cepwe464ebk6">
      <img src={src} alt={alt} loading="lazy" decoding="async" className="w-full h-auto" data-id="element-839" data-mp="2ginvievg3md8" />
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-840" data-mp="2qfskemgodvxq">
          {caption}
        </p>}
    </div>;
}
function VimeoEmbed({
  videoId,
  caption,
  aspect = 56.25
}: {
  videoId: string;
  caption?: string;
  aspect?: number;
}) {
  return <div className="bg-white rounded-2xl overflow-hidden border border-slate-200" data-id="element-841" data-mp="395s7x4wvb2z1">
      <div style={{
      padding: `${aspect}% 0 0 0`,
      position: 'relative'
    }} data-id="element-842" data-mp="193sl6x3leti3">
        <iframe src={`https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479`} frameBorder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
      }} title={caption || 'Video'} data-id="element-843" data-mp="153qj4mty3y2a" />
      </div>
      {caption && <p className="text-xs uppercase tracking-[0.2em] text-slate-500 px-6 py-4 text-center bg-slate-50 border-t border-slate-100" style={{
      fontFamily: "'Ronzino', sans-serif"
    }} data-id="element-844" data-mp="21qlp1oca48wb">
          {caption}
        </p>}
    </div>;
}
const processSteps = [{
  number: '01',
  title: 'Design system architecture',
  desc: 'Led the move from Sketch to Figma. Built a modern design system with reusable components for complex data tables, network topologies, and policy configurations.'
}, {
  number: '02',
  title: 'Threat visualization',
  desc: 'Rebuilt the dashboard to surface critical security events on arrival. Anomalies highlighted, one-click mitigation, no more digging through logs.'
}, {
  number: '03',
  title: 'Policy management',
  desc: 'Designed a visual builder for micro-segmentation. Admins could see the impact of a policy change before it touched production.'
}, {
  number: '04',
  title: 'User research and journey mapping',
  desc: 'Ran 40 interviews across security professional archetypes. Built a service-blueprint hybrid that became the source of truth for design decisions.'
}];
export function CaseStudyCyberSecurity({
  isOverlay = false
}: {
  isOverlay?: boolean;
}) {
  const navigate = useNavigate();
  const {
    viewPreference
  } = usePortfolio();
  useCaseStudyNavigation();
  useScrollAnimation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  return <>
      <ReadingProgress color="#016768" data-id="element-845" data-mp="2m4n4cjikxj6f" />
      {!isOverlay && <CustomCursor data-id="element-846" data-mp="2h2xrcybsqvfp" />}

      <div className="flex min-h-screen bg-white text-slate-900" data-id="element-847" data-mp="2h3h5e3ybszog">
        <div className="flex-1 min-w-0 flex flex-col relative pb-20 lg:pb-0" data-id="element-848" data-mp="2d371cwu3gjc8">
          {!isOverlay && <CaseStudyHeader data-id="element-849" data-mp="28adykei6rl2s" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-850" data-mp="289ce5jfa5m8e" />}

          <main className="flex-1 w-full pb-32" data-id="element-851" data-mp="23bsyqg6uj3ev">
            {/* HEADER BLOCK */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="pt-32 sm:pt-40 pb-16 sm:pb-24 px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full" data-id="element-852" data-mp="1ydho438q8ovl">
              <button type="button" onClick={() => navigate(viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional')} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-slate-900 hover:text-slate-500 transition-colors mb-10 group" style={{
              fontFamily: "'Ronzino', sans-serif"
            }} data-id="element-853" data-mp="4u91btgpgtbu">
                <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" data-id="element-854" data-mp="3r924xbiwibmy" />
                Back to home
              </button>

              <div className="mb-8" data-id="element-855" data-mp="3w2mwakpzo7d6">
                <ShieldCheck className="w-8 h-8 text-[#016768]" weight="light" data-id="element-856" data-mp="2ym7kufpzxea0" />
              </div>

              <h1 className="text-3xl sm:text-5xl md:text-6xl font-light text-[#016768] leading-[1.1] tracking-tight mb-4" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-857" data-mp="33ifz7qwdd3g4">
                Unisys Stealth
              </h1>

              <p className="text-lg sm:text-xl text-slate-500 font-light mb-8" style={{
              fontFamily: "'Gefika', sans-serif"
            }} data-id="element-858" data-mp="35bgxo5lhxrs0">
                Turning a powerful security platform into one teams could
                actually move through.
              </p>

              <p className="text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl" style={{
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }} data-id="element-859" data-mp="2mjeoffb6pdrs">
                I led the v5 to v6 redesign of Unisys Stealth, an enterprise
                cyber security platform. The legacy version was powerful but
                punishing: engineering-driven UI, technical jargon, and a
                learning curve steep enough to slow critical security decisions.
                v6 was about making it usable on a real shift, without
                sacrificing the depth specialists relied on.
              </p>
            </motion.section>

            {/* HERO IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-16 sm:mb-24" data-id="element-860" data-mp="1arp3krvqzm82">
              <div className="bg-slate-100 rounded-2xl py-10 sm:py-14 md:py-16 px-6 sm:px-10 md:px-14 flex items-center justify-center" data-id="element-861" data-mp="1tnfa8seml6sa">
                <img src="https://cdn.magicpatterns.com/uploads/8Z9WqxDbqCWDcRCMWd5zyF/Unisys.png" alt="Unisys Stealth v6 dashboard" className="w-full max-w-none object-contain drop-shadow-xl" data-id="element-862" data-mp="2hc814age4og1" />
              </div>
            </motion.section>

            {/* META STRIP */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-863" data-mp="cc6xnod5wrqs">
              <div className="border-t border-b border-slate-200 py-10" data-id="element-864" data-mp="100gai0see55s">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10" data-id="element-865" data-mp="v1vb23kyzpie">
                  <div data-id="element-866" data-mp="r0ta9iben5eb">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-867" data-mp="r1bd4xskohv7">
                      Product
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-868" data-mp="1rbvz30s5blji">
                      Unisys Stealth (v5 to v6)
                    </div>
                  </div>
                  <div data-id="element-869" data-mp="2a12cc8a67kxx">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-870" data-mp="29zr6m2x60346">
                      My role
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-871" data-mp="2nzs73h9ip8j4">
                      Product Designer
                    </div>
                  </div>
                  <div data-id="element-872" data-mp="229sgbk83g7iz">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-873" data-mp="2298y02ej7h4f">
                      Timeline
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-874" data-mp="2gabkuhdcovge">
                      6 months
                    </div>
                  </div>
                  <div data-id="element-875" data-mp="2uxwku2exiek3">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-876" data-mp="2v0afia5p5or4">
                      Skills
                    </div>
                    <div className="text-base font-medium text-slate-900" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-877" data-mp="oa74hdpqg4sj">
                      Design systems, Research, Enterprise UX, Competitive
                      analysis
                    </div>
                  </div>
                </div>
                <div className="pt-10 border-t border-slate-200" data-id="element-878" data-mp="170f3bjmeg92x">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#016768] mb-2" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-879" data-mp="1731d7pwrj0ob">
                    Team
                  </div>
                  <div className="text-base font-medium text-slate-900" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif'
                }} data-id="element-880" data-mp="1uscbn0qn4iza">
                    Mateo Cohen (Product Designer), Unisys product team, Agency
                    engineering partners
                  </div>
                </div>
              </div>
            </motion.section>

            {/* QUOTE-OVERLAY IMAGE CARD */}
            <motion.section initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-50px'
          }} variants={fadeUpVariant} className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full mb-24 sm:mb-32" data-id="element-881" data-mp="3fek6itwdvyau">
              <div className="relative rounded-2xl min-h-[280px] sm:h-[480px] sm:aspect-auto overflow-hidden flex items-center justify-center p-6 sm:p-16" data-id="element-882" data-mp="2wh0z1upkqnqj">
                <div className="absolute inset-0 bg-slate-900" aria-hidden="true" data-id="element-883" data-mp="ppiaabnkvxmy" />

                <div className="relative z-10 text-center max-w-3xl mx-auto" data-id="element-884" data-mp="14tl27b55goxz">
                  <p className="text-lg sm:text-3xl md:text-4xl font-light italic text-white leading-snug" style={{
                  fontFamily: "'Gefika', sans-serif"
                }} data-id="element-885" data-mp="zvjj4q7q4pmi">
                    "This is exactly what we needed to win sales meetings."
                  </p>
                  <div className="text-xs tracking-[0.2em] uppercase text-white/70 mt-6" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-886" data-mp="1nj12bxcxafdv">
                    Client business lead
                  </div>
                </div>
              </div>
            </motion.section>

            {/* CONTENT SECTIONS */}
            <section className="px-6 sm:px-12 md:px-16 lg:px-24 max-w-5xl mx-auto w-full space-y-24 sm:space-y-32" data-id="element-887" data-mp="29ltm8uxfk12">
              {/* Problem */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-888" data-mp="3vta9naiytva4">
                <div className="flex items-center gap-3 mb-4" data-id="element-889" data-mp="8lydiyzosra8">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#016768'
                }} data-id="element-890" data-mp="dmcg41iir90v">
                    01
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#016768'
                }} aria-hidden="true" data-id="element-891" data-mp="3m0l1opmmizjj" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#016768] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-892" data-mp="2yc1uyej82utk">
                  Problem: Powerful, but punishing
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-6 sm:mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-893" data-mp="2gud4lb5el38q">
                  Stealth v5 was technically deep but built for engineers, not
                  operators. Security professionals worked 8-hour shifts in
                  low-lit rooms, constantly toggling between dark-mode command
                  lines and Stealth's bright, cluttered interface. The visual
                  hierarchy fought them, the jargon slowed them down, and the
                  threat data they needed was buried.
                </p>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-8" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-894" data-mp="1k5nj7aukhsi0">
                  On the sales side, Illumio was winning meetings on first
                  impression. We needed a redesign that could hold up in a demo
                  and on a 3 a.m. incident response, both.
                </p>

                <ProofImage src="https://cdn.magicpatterns.com/uploads/asQNUakddLpBCPbCr2XyP6/Stealth-image-1.jpg" alt="Unisys Stealth v5 legacy interface" caption="Before: Stealth v5" data-id="element-895" data-mp="1t92fiu79ias2" />
              </motion.div>

              {/* Research */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-896" data-mp="1ofq2lhby3sf5">
                <div className="flex items-center gap-3 mb-4" data-id="element-897" data-mp="5i3bz236ycj1">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#016768'
                }} data-id="element-898" data-mp="5g9zv79t703n">
                    02
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#016768'
                }} aria-hidden="true" data-id="element-899" data-mp="3iov5o7n7s5z4" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#016768] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-900" data-mp="3rr4emt2u3rae">
                  Research: 40 interviews across security archetypes
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-901" data-mp="122vf0iz9wzqx">
                  I ran 40 user interviews across different security
                  professional archetypes, then built a journey map and service
                  blueprint hybrid. That artifact became the source of truth.
                  Every design decision had to map back to a real workflow and a
                  real engineering constraint. When the business pushed for a
                  cleaner-but-shallower direction, the blueprint pushed back.
                </p>
              </motion.div>

              {/* Process Timeline */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-902" data-mp="2iqs7d3mr8aup">
                <div className="flex items-center gap-3 mb-4" data-id="element-903" data-mp="gw490vt2agqy">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#016768'
                }} data-id="element-904" data-mp="c4vx9t49ejam">
                    03
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#016768'
                }} aria-hidden="true" data-id="element-905" data-mp="zt5f6jycqmwh" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#016768] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-906" data-mp="lufiv6jcwr96">
                  Process: Four design moves
                </h3>
                <p className="text-base sm:text-lg text-slate-700 leading-relaxed mb-10" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-907" data-mp="36n0siz0m2lm3">
                  Six months of work, condensed into the moves that did the
                  most.
                </p>

                <div data-id="element-908" data-mp="1t6cirw9kvrxd">
                  {processSteps.map((step, idx) => <div key={step.number} className="relative flex gap-5 sm:gap-8 pb-12 sm:pb-16 last:pb-0" data-id="element-909" data-mp="2pu0ofesgds01" data-mpi={idx}>
                      {idx !== processSteps.length - 1 && <div className="absolute left-5 sm:left-6 top-10 sm:top-12 bottom-0 w-px bg-slate-200" aria-hidden="true" data-id="element-910" data-mp="2gsj2hfsd6w3f" />}
                      <div className="relative z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-slate-300 flex items-center justify-center flex-shrink-0" data-id="element-911" data-mp="10gdnvk8pd0ks">
                        <span className="text-xs sm:text-sm font-medium text-[#016768]" style={{
                      fontFamily: "'Ronzino', sans-serif"
                    }} data-id="element-912" data-mp="qa2pfj7fc3g2">
                          {step.number}
                        </span>
                      </div>
                      <div className="flex-1 pt-1.5 sm:pt-2.5" data-id="element-913" data-mp="191vi1cikzmn9">
                        <h4 className="text-lg sm:text-xl font-medium text-slate-900 mb-2" style={{
                      fontFamily: "'Gefika', sans-serif"
                    }} data-id="element-914" data-mp="1e18emoz98831">
                          {step.title}
                        </h4>
                        <p className="text-base text-slate-600 leading-relaxed max-w-2xl" style={{
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }} data-id="element-915" data-mp="3ktsdoaafzgna">
                          {step.desc}
                        </p>
                      </div>
                    </div>)}
                </div>
              </motion.div>

              {/* Outcome */}
              <motion.div initial="hidden" whileInView="visible" viewport={{
              once: true,
              margin: '-50px'
            }} variants={fadeUpVariant} data-id="element-916" data-mp="3mo4kz37vg2x5">
                <div className="flex items-center gap-3 mb-4" data-id="element-917" data-mp="ea5ps3sq4lkp">
                  <span className="text-xs tracking-[0.2em] uppercase font-medium" style={{
                  fontFamily: "'Ronzino', sans-serif",
                  color: '#016768'
                }} data-id="element-918" data-mp="ebqjjr77s0hi">
                    04
                  </span>
                  <span className="h-0.5 w-8 rounded-full" style={{
                  backgroundColor: '#016768'
                }} aria-hidden="true" data-id="element-919" data-mp="11xj4h3o3v55k" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-medium text-[#016768] mb-6" style={{
                fontFamily: "'Gefika', sans-serif"
              }} data-id="element-920" data-mp="syp9tiuc4oun">
                  Outcome: v6 shipped
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-8 mb-8 border-y border-slate-100" data-id="element-921" data-mp="nz2v14yuz0jq">
                  <div data-id="element-922" data-mp="a7y36p1qfcjy">
                    <div className="text-lg font-medium text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-923" data-mp="e7oqgis1vajq">
                      Competitive positioning
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-924" data-mp="17k6nvgnujznx">
                      Redesign positioned Unisys competitively against Illumio
                      with simplified workflows that kept technical depth
                      intact.
                    </div>
                  </div>
                  <div data-id="element-925" data-mp="orklsemcjcuw">
                    <div className="text-lg font-medium text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-926" data-mp="juc9gunvq31k">
                      Strategic validation
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-927" data-mp="2a7jl6odt9f9t">
                      The CrowdStrike partnership I proposed during the design
                      phase was later implemented by the client.
                    </div>
                  </div>
                  <div data-id="element-928" data-mp="2miat6z0vb2go">
                    <div className="text-lg font-medium text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-929" data-mp="2rgm4le6t9l10">
                      Design system adoption
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-930" data-mp="23r9nls58s8o5">
                      The full component library was adopted for v6 launch and
                      became the foundation for the next platform release.
                    </div>
                  </div>
                  <div data-id="element-931" data-mp="1k1gvbjbxrao1">
                    <div className="text-lg font-medium text-slate-900 mb-2" style={{
                    fontFamily: "'Gefika', sans-serif"
                  }} data-id="element-932" data-mp="1k4uxaqgothuy">
                      Sales impact
                    </div>
                    <div className="text-sm text-slate-600 leading-relaxed" style={{
                    fontFamily: 'system-ui, -apple-system, sans-serif'
                  }} data-id="element-933" data-mp="1o2slpr2mvzxa">
                      The redesign became the lead artifact in client demos,
                      shifting first impressions of the product on every call.
                    </div>
                  </div>
                </div>

                {/* What shipped — walkthrough video */}
                <div className="mb-10" data-id="element-934" data-mp="cq89yh19iwwn">
                  <VimeoEmbed videoId="476985592" caption="Walkthrough: Stealth v6, end to end" data-id="element-935" data-mp="cp6mfd328hy1" />
                </div>

                <p className="text-base sm:text-lg text-slate-700 leading-relaxed" style={{
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }} data-id="element-936" data-mp="2t4bp4wyxm7qf">
                  v6 modernized the platform, cut the learning curve for new
                  admins, and gave the sales team a product they could lead
                  with.
                </p>
              </motion.div>
            </section>

            <CaseStudyNavigation previousCase={{
            category: 'Financial Services',
            title: 'US Bank Commercial Card',
            slug: 'us-bank'
          }} nextCase={{
            category: 'AI Comparison',
            title: 'Watson vs Dialogflow',
            slug: 'ai-comparison'
          }} data-id="element-937" data-mp="2y3ei3j1n0ni1" />
          </main>
        </div>
      </div>

      <FloatingButtons data-id="element-938" data-mp="2jbfc64vq204d" />
      <ChatDrawer data-id="element-939" data-mp="2eb10mcuww81l" />
      <MobileBottomCTA data-id="element-940" data-mp="2eahudtemj83e" />
      {!isOverlay && <Footer data-id="element-941" data-mp="2e9g6gocier49" />}
    </>;
}